import { Grid, Modal, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  Button,
  PageTitleAndSubTitle,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import SvgClose from '@HomaHealth/ui-component-library/lib/_components/atoms/Icons/Close';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React, { useEffect, useState } from 'react';
import { downloadMultipleReports } from '../../../core-utils/helper-utils/downloadPdfHelper';
import componentConstants from '../../../core-utils/messages/componentConstants';
import {
  CARE_QUALITY,
  CLAIMS_METRICS,
  DISEASE_PRELAVENCE,
  CHRONIC_PREVALENCE,
  ENROLLMENT_METRICS,
  HIGH_COST_CLAIMS,
  PLAN_PERFORMANCE,
  PATIENT_ADMISSION,
  PROCEDURE_UTILIZATION,
  PROVIDER_UTILIZATION,
  REJECTED_CLAIMS,
  RX_UTILIZATION,
  REPORT_PLAN,
  STOPLOSS_TRIGGERS,
  SUMMARY,
  EPISODES_OF_CARE,
  // WASTE_CALCULATOR,
  STOPLOSS_REPORT,
} from '../../../core-utils/routes';
import { useStores } from '../../../stores';
import Snackbar from '../../atoms/Snackbar';
import CheckboxGroup from '../../molecules/CheckboxGroup';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import { observer } from 'mobx-react';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import { useAPIPayload } from '../../../core-utils/helper-utils/apiPayloadHelper';

export interface ReportsModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  startDate: string;
  endDate: string;
  deductibleAmount?: number;
  riskProbability?: number;
  members_list?: string[];
  networkIndicator?: string[];
}

const ReportsModal: React.FC<ReportsModalProps> = observer(
  ({
    open,
    setOpen,
    startDate,
    endDate,
    deductibleAmount,
    riskProbability,
    members_list,
    networkIndicator,
  }) => {
    const useStyles = makeStyles()({
      modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      paper: {
        width: '1106px',
        height: '542px',
        padding: '24px 28px 40px 32px',
        backgroundColor: patientAccessTheme.palette.status.white,
        border: 'none !important',
        position: 'relative',
      },
      button: {
        padding: '0px',
      },
      scroll: scrollBarStyles,
    });

    const { classes } = useStyles();
    const pathName: string =
      '/' +
      window.location.pathname?.substring(
        window.location.pathname.lastIndexOf('/') + 1,
      );
    const messages = componentConstants.ORGANISMS.REPORTS_MODAL;
    const [finalReports, setFinalReports] = useState<string[]>([]);
    const employerGroupStore = useStores().employerGroupStore;
    const navigationStore = useStores().navigationStore;

    const payload = useAPIPayload();
    const requestBody = {
      startDate: startDate,
      endDate: endDate,
      maxClaimDate:
        employerGroupStore?.employerGroupResponseData?.max_claim_date,
      employer:
        employerGroupStore?.employerGroupResponseData?.employer_group_name,
      deductible_amount:
        deductibleAmount ?? employerGroupStore?.deductibleAmount,
      riskProbability: riskProbability ?? 0.7,
      reports: finalReports,
      claim_id_list: [],
      member_unique_id_list: [],
      member_ids_crossing_50_percent_deductible:
        members_list ??
        employerGroupStore?.memberIdsCrossing50PercentDeductible,
      ...payload,
      networkIndicator:
        networkIndicator ??
        employerGroupStore.employerGroupResponseData.network_indicator_list,
    };

    const [summary, setSummary] = useState([
      {
        id: 'summaryGroup',
        name: 'State Of Health',
        checked: false,
      },
      {
        id: 'summaryReport',
        name: 'Executive Summary',
        checked: pathName === SUMMARY,
      },
    ]);

    const [diseaseManagement, setDiseaseManagement] = useState([
      {
        id: 'diseaseManagementGroup',
        name: 'Disease Management',
        checked: false,
      },
      {
        id: 'diseasePrevalenceReport',
        name: 'Disease Prevalence',
        checked: pathName === DISEASE_PRELAVENCE,
      },
      {
        id: 'chronicPrevalenceReport',
        name: 'Chronic Prevalence',
        checked: pathName === CHRONIC_PREVALENCE,
      },
      // {
      //   id: 'riskReport',
      //   name: 'Disease Prognosis',
      //   checked: pathName === RISING_RISK,
      // },
      {
        id: 'careQualityReport',
        name: 'Care Gaps',
        checked: pathName === CARE_QUALITY,
      },
    ]);

    const [costManagement, setCostManagement] = useState([
      {
        id: 'costManagementGroup',
        name: 'Cost Management',
        checked: false,
      },
      {
        id: 'episodesOfCareReport',
        name: 'Episodes of Care',
        checked: pathName === EPISODES_OF_CARE,
      },
      // {
      //   id: 'wasteCalculatorReport',
      //   name: 'Waste Calculator',
      //   checked: pathName === WASTE_CALCULATOR,
      // },
      {
        id: 'highCostReport',
        name: 'High Cost Claimants',
        checked: pathName === HIGH_COST_CLAIMS,
      },
    ]);

    const [stopLossManagement, setStopLossManagement] = useState([
      {
        id: 'stopLossManagementGroup',
        name: 'Stop Loss Management',
        checked: false,
      },
      {
        id: 'stoplossAggregateReport',
        name: 'Stop Loss Aggregate Report',
        checked: pathName === STOPLOSS_REPORT,
      },
      {
        id: 'stoploss5050Report',
        name: '50-50 Report Plan',
        checked: pathName === REPORT_PLAN,
      },
      {
        id: 'stopLossReport',
        name: 'Stop Loss Triggers',
        checked: pathName === STOPLOSS_TRIGGERS,
      },
    ]);

    const [planAnalysis, setPlanAnalysis] = useState([
      {
        id: 'planAnalysisGroup',
        name: 'Plan Analysis',
        checked: false,
      },
      {
        id: 'planPerformanceReport',
        name: 'Summary',
        checked: pathName === PLAN_PERFORMANCE,
      },
      {
        id: 'claimsReport',
        name: 'Claims Metrics',
        checked: pathName === CLAIMS_METRICS,
      },
      {
        id: 'enrollmentReport',
        name: 'Enrollment Metrics',
        checked: pathName === ENROLLMENT_METRICS,
      },
      // {
      //   id: 'networkLeakageReport',
      //   name: 'Network Leakage',
      //   checked: pathName === NETWORK_LEAKAGE,
      // },
    ]);

    const [claimsManagement, setClaimsManagement] = useState([
      {
        id: 'claimsManagementGroup',
        name: 'Claims Management',
        checked: false,
      },
      {
        id: 'rejectedClaimsReport',
        name: 'Rejected Claims',
        checked: pathName === REJECTED_CLAIMS,
      },
      // {
      //   id: 'pendingClaimsReport',
      //   name: 'Pending Claims',
      //   checked: pathName === PENDING_CLAIMS,
      // },
    ]);

    const [medicalClaimUtilization, setMedicalClaimUtilization] = useState([
      {
        id: 'medicalClaimUtilizationGroup',
        name: 'Medical Claim Utilization',
        checked: false,
      },
      // {
      //   id: 'utilizationSummaryReport',
      //   name: 'Summary',
      //   checked: pathName === UTILIZATION_METRICS,
      // },
      {
        id: 'utilizationPatientReport',
        name: 'Admission Utilization',
        checked: pathName === PATIENT_ADMISSION,
      },
      // {
      //   id: 'utilizationPharmacyReport',
      //   name: 'Pharmacy Utilization',
      //   checked: pathName === RX_UTILIZATION,
      // },
      {
        id: 'utilizationProcedureReport',
        name: 'Procedure Utilization',
        checked: pathName === PROCEDURE_UTILIZATION,
      },
      {
        id: 'utilizationProviderReport',
        name: 'Provider Utilization',
        checked: pathName === PROVIDER_UTILIZATION,
      },
    ]);

    const [drugUtilization, setDrugUtilization] = useState([
      {
        id: 'drugUtilizationGroup',
        name: 'Drug Utilization',
        checked: false,
      },
      {
        id: 'utilizationPharmacyReport',
        name: 'Drug Utilization Summary',
        checked: pathName === RX_UTILIZATION,
      },
      // {
      //   id: 'utilizationSavingsReport',
      //   name: 'Savings Utilization',
      //   checked: pathName === SAVINGS_UTILIZATION,
      // },
    ]);

    const getAllCheckedIds = (dataArray: any) => {
      const idsArray: string[] = [];
      dataArray?.map((item: any, index: number) => {
        if (index !== 0 && item.checked === true) {
          idsArray.push(item.id);
        }
      });

      return idsArray;
    };

    const getFinalSelectedReports = () => {
      let finalReportsIds: string[] = [];
      finalReportsIds = getAllCheckedIds(summary)?.concat(
        getAllCheckedIds(diseaseManagement),
        getAllCheckedIds(costManagement),
        getAllCheckedIds(stopLossManagement),
        getAllCheckedIds(planAnalysis),
        getAllCheckedIds(claimsManagement),
        getAllCheckedIds(medicalClaimUtilization),
        getAllCheckedIds(drugUtilization),
      );

      return finalReportsIds;
    };

    useEffect(() => {
      setFinalReports(getFinalSelectedReports());
    }, [
      summary,
      diseaseManagement,
      costManagement,
      stopLossManagement,
      planAnalysis,
      claimsManagement,
      medicalClaimUtilization,
      drugUtilization,
    ]);

    const handleClose = () => {
      setOpen(false);
    };

    const [snackbarState, setSnackbarState] = React.useState<{
      open: boolean;
      message: string;
      severity: MuiColor | undefined;
      duration: null | number;
    }>({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });

    const handleCloseSnackBar = () => {
      setSnackbarState({
        open: false,
        message: '',
        severity: undefined,
        duration: null,
      });
    };

    const handleOpenSnackBar = (
      message: string,
      severity: MuiColor,
      duration: number | null,
    ) => {
      setSnackbarState({
        open: true,
        message: message,
        severity: severity,
        duration: duration,
      });
    };

    return (
      <>
        <Modal open={open} onClose={handleClose} className={classes.modal}>
          <Paper className={classes.paper}>
            <Grid
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '49px',
              }}
            >
              <PageTitleAndSubTitle
                title={messages.TITLE}
                subTitle={messages.SUBTITLE}
              />
              <SvgClose
                style={{ cursor: 'pointer' }}
                color={patientAccessTheme.palette.status.icon}
                onClick={handleClose}
              />
            </Grid>
            <Grid
              className={classes.scroll}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '30px',
                height: '62%',
                overflowY: 'scroll',
              }}
            >
              <CheckboxGroup
                items={summary}
                width="232px"
                setItems={setSummary}
              />
              <CheckboxGroup
                items={diseaseManagement}
                width="232px"
                setItems={setDiseaseManagement}
              />
              <CheckboxGroup
                items={costManagement}
                width="232px"
                setItems={setCostManagement}
              />
              <CheckboxGroup
                items={stopLossManagement}
                width="232px"
                setItems={setStopLossManagement}
              />
              <CheckboxGroup
                items={planAnalysis}
                width="232px"
                setItems={setPlanAnalysis}
              />
              <CheckboxGroup
                items={claimsManagement}
                width="232px"
                setItems={setClaimsManagement}
              />
              <CheckboxGroup
                items={medicalClaimUtilization}
                width="232px"
                setItems={setMedicalClaimUtilization}
              />

              <CheckboxGroup
                items={drugUtilization}
                width="232px"
                setItems={setDrugUtilization}
              />
            </Grid>
            <Grid
              style={{
                display: 'flex',
                justifyContent: 'right',
                margin: '0px 40px 40px 0px',
                position: 'absolute',
                right: '0px',
                bottom: '0px',
              }}
            >
              <Button
                label={
                  <Typography
                    textColor={patientAccessTheme.palette.primary[500]}
                    fontSize="12px"
                    lineHeight="12px"
                    fontType={'medium'}
                  >
                    {messages.VIEW_BUTTON}
                  </Typography>
                }
                variant="outlined"
                height="36px"
                width="180px"
                styleProps={{ marginRight: '25px' }}
                borderRadius="4px"
                className={classes.button}
                onClick={() => {
                  downloadMultipleReports(
                    'reports',
                    requestBody,
                    handleOpenSnackBar,
                    'open',
                  );
                  setOpen(false);
                  handleOpenSnackBar(
                    messages.SNACKBAR_MESSAGES.OPEN_REPORT,
                    'info',
                    6000,
                  );
                  navigationStore.handleOpenPopUpNotifier(true);
                }}
              />
              <Button
                label={
                  <Typography
                    textColor={patientAccessTheme.palette.status.white}
                    fontSize="12px"
                    lineHeight="12px"
                    fontType={'medium'}
                  >
                    {messages.DOWNLOAD_BUTTON}
                  </Typography>
                }
                variant="contained"
                color="primary"
                height="36px"
                width="180px"
                borderRadius="4px"
                className={classes.button}
                onClick={() => {
                  downloadMultipleReports(
                    'reports',
                    requestBody,
                    handleOpenSnackBar,
                    'download',
                  );
                  setOpen(false);
                  handleOpenSnackBar(
                    messages.SNACKBAR_MESSAGES.DOWNLOAD_REPORT,
                    'info',
                    6000,
                  );
                  navigationStore.handleOpenPopUpNotifier(true);
                }}
              />
            </Grid>
          </Paper>
        </Modal>
        <Snackbar
          {...snackbarState}
          vertical={'bottom'}
          horizontal={'right'}
          handleClose={handleCloseSnackBar}
        />
      </>
    );
  },
);

export default ReportsModal;
